import { useCharacterProgressionStore } from '../store/characterProgressionSlice';
import type { TalentNodeId } from '../types/talent.types';

/**
 * Helpers consumed by the services that apply gameplay effects tied to the
 * **talent notables** (cf. content-bible spec §9 "21 notables").
 *
 * Active wiring:
 * - `LocationService.checkTrapAtPosition` consumes `hasPasDeLoup` (B7).
 * - `ShopService.getItemPrice` consumes `hasFrugal` (−15%).
 * - `LootService.getTalentLootMultiplier` consumes `hasPillard` (×1.2).
 * - `LocationPrompt` consumes `hasOeilDeSage` to show the loot pool preview
 *   of a `spawnItemsFromPool` location (chests / urns).
 *
 * The ids follow the spec convention: `talent-notable-<name-kebab>`.
 */

/** Returns true if the notable node `nodeId` is taken in the tree. */
export function hasTalentNotable(nodeId: TalentNodeId): boolean {
  return useCharacterProgressionStore.getState().pointsSpent.includes(nodeId);
}

export const TalentEffects = {
  hasFrenzy: () => hasTalentNotable('talent-notable-frenzy'),
  hasPrecision: () => hasTalentNotable('talent-notable-precision'),
  hasSaigneur: () => hasTalentNotable('talent-notable-bloodletter'),
  hasLanceVivante: () => hasTalentNotable('talent-notable-living-spear'),
  hasCriDeGuerre: () => hasTalentNotable('talent-notable-war-cry'),
  hasVivace: () => hasTalentNotable('talent-notable-hardy'),
  hasBouclierDEsprit: () => hasTalentNotable('talent-notable-spirit-shield'),
  hasEndurance: () => hasTalentNotable('talent-notable-endurance'),
  hasPlaiesOuvertes: () => hasTalentNotable('talent-notable-open-wounds'),
  hasReflexes: () => hasTalentNotable('talent-notable-reflexes'),
  hasVoyageur: () => hasTalentNotable('talent-notable-traveler'),
  hasOeilDeSage: () => hasTalentNotable('talent-notable-sage-eye'),
  hasPasDeLoup: () => hasTalentNotable('talent-notable-wolf-step'),
  hasFrugal: () => hasTalentNotable('talent-notable-frugal'),
  hasPillard: () => hasTalentNotable('talent-notable-plunderer'),
  hasEmbrasement: () => hasTalentNotable('talent-notable-blaze'),
  hasBanquise: () => hasTalentNotable('talent-notable-ice-field'),
  hasTempete: () => hasTalentNotable('talent-notable-storm'),
  hasSolTremblant: () => hasTalentNotable('talent-notable-quaking-ground'),
  hasBourrasque: () => hasTalentNotable('talent-notable-squall'),
  hasVoileSombre: () => hasTalentNotable('talent-notable-dark-veil'),
};
